import { InputFormValues } from '@/modules/CombinedLogs/types'
import { useController, UseControllerProps } from 'react-hook-form'
import styles from './TableFormTripInput.module.scss'
import { useState, KeyboardEvent, useEffect, useMemo, useRef } from 'react'
import {
    autoUpdate,
    flip,
    FloatingNode,
    offset,
    useClick,
    useDismiss,
    useFloating,
    useFloatingNodeId,
    useInteractions,
    useTransitionStatus,
} from '@floating-ui/react'
import { Input } from '@/ui/Input'
import clsx from 'clsx'
import { useGetTrips } from '@/modules/CombinedLogs/api/useGetTrips'
import { formatTripValues } from '@/modules/CombinedLogs/helpers/formatTripValues'
import { IRtnTripAsNumber } from '@/types/logs'

interface Props extends Omit<UseControllerProps<InputFormValues>, 'name'> {
    name: keyof InputFormValues
    setFocused: (isFocused: boolean) => void
    selectedTrips: IRtnTripAsNumber[]
    setSelectedTrips: (trips: IRtnTripAsNumber[]) => void
    hidden: boolean
    isBulkCurtailment?: boolean
    trip?: string
}

export const TableFormRtnInput = ({ setFocused, selectedTrips, setSelectedTrips, hidden, trip, ...props }: Props) => {
    const { field } = useController({ control: props.control, name: props.name })
    const { data: trips } = useGetTrips(trip)
    const [isOpen, setIsOpen] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)
    const nodeId = useFloatingNodeId()
    const rootRef = useRef<HTMLDivElement>(null)

    const { x, y, strategy, context, refs } = useFloating({
        nodeId,
        open: isOpen,
        onOpenChange: setIsOpen,
        whileElementsMounted: (reference, floating, update) => {
            const cleanup = autoUpdate(reference, floating, update)
            const handleScroll = (event: Event) => {
                if (floating && event.target === floating) return
                setIsOpen(false)
            }

            const scrollContainer = rootRef.current?.closest('[data-scrollable-table]')
            if (scrollContainer) {
                scrollContainer.addEventListener('scroll', handleScroll)
            }

            return () => {
                cleanup()
                if (scrollContainer) {
                    scrollContainer.removeEventListener('scroll', handleScroll)
                }
            }
        },
        middleware: [offset({ mainAxis: 5 }), flip()],
        placement: 'bottom-start',
    })

    const dismiss = useDismiss(context)
    const click = useClick(context)

    const { isMounted, status } = useTransitionStatus(context)
    const { getFloatingProps } = useInteractions([click, dismiss])

    const allTrips = useMemo(() => {
        return trips ? [...trips.inbound, ...trips.outbound] : []
    }, [trips])

    const handleSelect = (selectedValue: IRtnTripAsNumber) => {
        if (!field.onChange) {
            return
        }

        if (selectedTrips.some((trip) => trip.id === selectedValue.id)) {
            const newTrips = selectedTrips.filter((trip) => trip.id !== selectedValue.id)
            setSelectedTrips(newTrips)
            field.onChange([newTrips.length ? formatTripValues(newTrips) : ''])
        } else {
            const newTrips = [...selectedTrips, selectedValue]
            setSelectedTrips(newTrips)
            field.onChange([newTrips.length ? formatTripValues(newTrips) : ''])
        }
    }

    const handleToTheEndOfRun = () => {
        if (!trips) return
        const latestSelectedTrip = selectedTrips.sort((a, b) => b.order - a.order)[0]
        const followingTrips = allTrips.filter((trip) => trip.order > latestSelectedTrip.order)
        const tripsToSelect = [...selectedTrips, ...followingTrips]

        setSelectedTrips(tripsToSelect)
        field.onChange([formatTripValues(tripsToSelect)])
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            setIsOpen(false)
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setHighlightedIndex((prev) => (prev === null || prev === allTrips.length - 1 ? 0 : prev + 1))
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault()
            setHighlightedIndex((prev) => (prev === null || prev === 0 ? allTrips.length - 1 : prev - 1))
        }
        if (e.key === 'Enter' && highlightedIndex !== null) {
            e.preventDefault()
            handleSelect(allTrips[highlightedIndex])
        }
        if (e.key === 'Tab') {
            setIsOpen(false)
        }
    }

    const handleFocus = () => {
        setIsOpen(true)
    }

    useEffect(() => {
        setFocused(isOpen)
    }, [isOpen])

    return (
        <div onKeyDown={handleKeyDown} ref={rootRef}>
            <div ref={refs.setReference}>
                <Input
                    value={field.value}
                    onChange={(e) => {
                        field.onChange(e.target.value)
                        if (e.target.value === '') {
                            setSelectedTrips([])
                        }
                    }}
                    onFocus={handleFocus}
                    variant="table"
                    fullWidth
                    placeholder="Rtn"
                    ref={field.ref}
                    transparent
                    className={styles.tripInput}
                    {...props}
                    tabIndex={hidden ? -1 : 0}
                />
            </div>

            <FloatingNode id={nodeId}>
                {isMounted && allTrips.length > 0 && (
                    <div
                        ref={refs.setFloating}
                        style={{ position: strategy, top: y ?? 0, left: x ?? 0, zIndex: 11 }}
                        data-status={status}
                        {...getFloatingProps()}
                    >
                        <div className={styles.dropdownContainer}>
                            <ul
                                className={clsx(styles.dropdownOptions, {
                                    [styles.dropdownOptionsOpen]: isOpen,
                                })}
                                role="listbox"
                            >
                                <div>
                                    {trips?.inbound?.length &&
                                        trips?.outbound?.length &&
                                        trips?.outbound?.[0]?.trip > trips?.inbound?.[0]?.trip && (
                                            <li className={styles.dropdownOption}></li>
                                        )}

                                    {trips?.outbound?.map((option, index) => (
                                        <li
                                            key={option.id}
                                            role="option"
                                            className={clsx(styles.dropdownOption, {
                                                [styles.highlighted]: highlightedIndex === index + trips.inbound.length,
                                                [styles.inactive]: selectedTrips.some((trip) => trip.id === option.id),
                                            })}
                                            onClick={() => handleSelect(option)}
                                            onMouseEnter={() => setHighlightedIndex(index + trips.inbound.length)}
                                        >
                                            {option.trip}
                                        </li>
                                    ))}
                                </div>

                                <div>
                                    {trips?.inbound.map((option, index) => (
                                        <li
                                            key={option.id}
                                            role="option"
                                            className={clsx(styles.dropdownOption, {
                                                [styles.highlighted]: highlightedIndex === index,
                                                [styles.inactive]: selectedTrips.some((trip) => trip.id === option.id),
                                            })}
                                            onClick={() => {
                                                handleSelect(option)
                                            }}
                                            onMouseEnter={() => setHighlightedIndex(index)}
                                        >
                                            {option.trip}
                                        </li>
                                    ))}
                                </div>
                            </ul>
                            <button
                                type="button"
                                onClick={handleToTheEndOfRun}
                                disabled={selectedTrips.length === 0}
                                className={styles.toEndOfRunButton}
                            >
                                To end of run
                            </button>
                        </div>
                    </div>
                )}
            </FloatingNode>
        </div>
    )
}
